import { useState } from 'react';
import { useT } from '@/store';
import { useSites } from '@/hooks/useDirectory';
import { useDeleteSite, useRestoreSite, useSaveSite } from '@/hooks/useDataAdmin';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { CoordinateField } from '@/components/ui/CoordinateField';
import { Modal } from '@/components/ui/Overlay';
import { useAllClients } from '@/hooks/useClientRequests';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { parseDecimalInput } from '@/lib/format';
import { SmartImportModal } from './SmartImportModal';
import { ClientReliabilityModal } from './ClientReliabilityModal';
import { PromiseKeepingModal } from './PromiseKeepingModal';

const EMPTY_FORM = {
  client_id: '',
  client_name: '',
  site_name: '',
  lat: '',
  lng: '',
  contact_phone: '',
  contact_email: '',
};

/**
 * Client delivery sites.
 *
 * Each site carries a `portal_access_key` — the unguessable string that IS
 * the authentication for that client's public tracking page. It is shown as
 * a copyable tracking link rather than a raw key, because what a dispatcher
 * actually needs to do with it is send it to the client; and it is never
 * regenerated from this screen, since doing so would silently break every
 * link already sent.
 */
export function SiteList() {
  const t = useT();
  const [importing, setImporting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  // Salia patikimumo, nes tai to paties klausimo antra puse: vienas sako,
  // kiek galima pasitiketi objekto duomenimis, kitas — kiek klientas gali
  // pasitiketi mumis.
  const [promiseOpen, setPromiseOpen] = useState(false);
  const { data: sites = [], isLoading, isError, error } = useSites({
    includeInactive: showArchived,
  });
  const saveSite = useSaveSite();
  const [formError, setFormError] = useState(null);
  const deleteSite = useDeleteSite();
  const restoreSite = useRestoreSite();
  const [editing, setEditing] = useState(null);
  // For the client picker at the top of the modal. A site's client_id is
  // what decides whether its loads can be invoiced at all, and until V-48
  // the console could only set it on a site that already existed — see the
  // field's comment in the modal.
  // limit: 200 read as "give me all of them" and did not: core/paging.py
  // SNAPS an out-of-range limit to the nearest allowed page size (25/50/100),
  // so this asked for 200 and silently received 100. useAllClients() pages
  // through the whole list instead.
  const { data: clientCompanies = [] } = useAllClients();
  const [copiedKey, setCopiedKey] = useState(null);

  const submit = () => {
    // V-351, the same comma as QuarryList.submit() — and here it decides
    // the geofence a delivery is judged to have arrived inside, so a
    // truncated coordinate does not merely draw the pin wrong: dwell never
    // starts and the load never registers as delivered on GPS.
    const lat = parseDecimalInput(editing.lat);
    const lng = parseDecimalInput(editing.lng);
    // V-47, same as QuarryList: a bare `return` here meant the second
    // checklist step also failed in silence.
    if (!editing.client_name.trim()) return setFormError('data.errClientName');
    if (!editing.site_name.trim()) return setFormError('data.errSiteName');
    if (Number.isNaN(lat) || Number.isNaN(lng)) return setFormError('data.errCoordinates');
    setFormError(null);

    saveSite.mutate(
      {
        siteId: editing.siteId,
        client_name: editing.client_name.trim(),
        site_name: editing.site_name.trim(),
        lat,
        lng,
        contact_phone: editing.contact_phone.trim() || undefined,
        contact_email: editing.contact_email.trim() || undefined,
        // V-48: sent on CREATE as well now. It used to go only on an edit,
        // because create_site() did not accept it — so every site the
        // console made started unlinked and every load hauled to it was
        // unbillable until somebody reopened the site and noticed.
        client_id: editing.client_id || null,
        // Still only on an existing site: create_site() takes no dwell
        // override, and sending one there would be silently dropped.
        ...(editing.siteId
          ? {
              // '' clears the override so this site falls back to the
              // client's contract; '0' is a real term (billing starts the
              // moment the truck stops) and must survive as a number, which
              // is why this is not `Number(x) || null`.
              dwell_free_minutes_site:
                editing.dwell_free_minutes_site === '' ? null : Number(editing.dwell_free_minutes_site),
            }
          : null),
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  const copyPortalLink = async (site) => {
    const url = `${window.location.origin}/track/${site.portal_access_key}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(site.site_id);
      window.setTimeout(() => setCopiedKey(null), 2500);
    } catch {
      // Clipboard is blocked outside a secure context; fall back to showing
      // the link so it can still be copied by hand.
      window.prompt(t('data.portalLink'), url);
    }
  };

  return (
    <Panel
      title={t('data.sitesHeading')}
      actions={
        <>
          <label className="flex items-center gap-1.5 text-[11px] text-dim">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            {t('common.showArchived')}
          </label>
          {/* P1-1: sites are the highest-turnover object in this business —
              a new construction site most weeks — and each one needs a name,
              a client, coordinates and a geofence radius before a single load
              can be dispatched to it. */}
          <button type="button" className="btn" onClick={() => setImporting(true)}>
            {t('import.openBtn')}
          </button>
          <button type="button" className="btn" onClick={() => setReliabilityOpen(true)}>
            {t('clientReliability.openBtn')}
          </button>
          <button type="button" className="btn" onClick={() => setPromiseOpen(true)}>
            {t('promiseKeeping.openBtn')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing({ ...EMPTY_FORM })}>
            {t('common.addBtn')}
          </button>
        </>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : sites.length === 0 ? (
        <EmptyState>{t('data.noSitesYet')}</EmptyState>
      ) : (
        <ul className="space-y-2">
          {sites.map((site) => (
            <li
              key={site.site_id}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-[11px] border border-line bg-white/2 p-3 ${
                site.active ? '' : 'opacity-60'
              }`}
            >
              <div className="min-w-0 flex-1">
                {/* The name truncates, the badge does not: inside the
                    truncating element a long "client · site" pair would push
                    "(Archyvuota)" past the ellipsis and hide the one part of
                    the line the dispatcher most needs to see. */}
                <div className="flex items-center text-[13px] font-semibold">
                  <span className="min-w-0 truncate">
                    {site.client_name} · {site.site_name}
                  </span>
                  {site.active ? null : <ArchivedBadge deletedAt={site.deleted_at} />}
                </div>
                {/* V-48. The one thing about a site that costs money to get
                    wrong, said on the row rather than two clicks inside it.
                    An already-installed carrier has no other way to find
                    which of his twenty-five sites cannot be invoiced — the
                    draft names the loads, not the sites, and only after
                    they have been hauled. Archived rows are exempt: nothing
                    is being delivered to them. */}
                {site.active && !site.client_id ? (
                  <div className="mt-0.5 text-[11px] font-semibold leading-snug text-amber">
                    {t('data.siteNotLinked')}
                  </div>
                ) : null}
                <div className="truncate text-[11px] text-dim">
                  {site.contact_phone || '—'} · {site.lat?.toFixed(4)}, {site.lng?.toFixed(4)}
                </div>
              </div>

              <div className="flex gap-1.5">
                <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={() => copyPortalLink(site)}>
                  {copiedKey === site.site_id ? t('common.addedCheck') : t('data.copyPortalLink')}
                </button>
                <button
                  type="button"
                  className="btn !px-2.5 !py-1 !text-[11px]"
                  onClick={() =>
                    setEditing({
                      siteId: site.site_id,
                      client_id: site.client_id ?? '',
                      client_name: site.client_name ?? '',
                      site_name: site.site_name ?? '',
                      lat: String(site.lat ?? ''),
                      lng: String(site.lng ?? ''),
                      contact_phone: site.contact_phone ?? '',
                      contact_email: site.contact_email ?? '',
                      dwell_free_minutes_site:
                        site.dwell_free_minutes_site === null || site.dwell_free_minutes_site === undefined
                          ? ''
                          : String(site.dwell_free_minutes_site),
                    })
                  }
                >
                  {t('common.editBtn')}
                </button>
                {site.active ? (
                  <ConfirmButton
                    label={t('common.deleteBtn')}
                    confirmLabel={t('common.confirmDelete')}
                    hint={t('data.deleteSiteHint')}
                    onConfirm={() => deleteSite.mutate(site.site_id)}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn !px-2.5 !py-1 !text-[11px]"
                    disabled={restoreSite.isPending}
                    onClick={() => restoreSite.mutate(site.site_id)}
                  >
                    {t('common.restoreBtn')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {deleteSite.isError ? <ErrorState error={deleteSite.error} /> : null}
      {restoreSite.isError ? <ErrorState error={restoreSite.error} /> : null}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={t(editing?.siteId ? 'common.editBtn' : 'data.addSite')}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saveSite.isPending}>
              {t('common.saveBtn')}
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            {/* V-48. FIRST on the form, and offered when the site is
                CREATED rather than only when it is edited.

                This select is not a portal setting, which is what it used
                to be called. client_sites.client_id is the only thing
                core/invoicing.find_invoiceable() reads to decide who a
                delivered load can be billed to: leave it empty and every
                load hauled here lands in the `no_client` bucket — weighed,
                POD'd, priced and unreachable by the invoice button. On a
                fresh install that measured 1358.30 EUR from six loads.

                The free-text name below is a LABEL. It was the only client
                field on this form, it is required, and it is what the
                dispatcher's own lists read — so a carrier who filled it in
                had every reason to believe he had entered his client. He
                had not, and nothing said so until the first invoice. */}
            <Field label={t('data.siteClientLabel')}>
              <select
                className="field"
                value={editing.client_id}
                onChange={(e) => {
                  const clientId = e.target.value;
                  const picked = clientCompanies.find((c) => c.client_id === clientId);
                  setEditing({
                    ...editing,
                    client_id: clientId,
                    // Only ever written when the choice CHANGES to a real
                    // company, so opening a site and saving something else
                    // never quietly rewrites a label the carrier chose.
                    // Unlinking keeps the label — it is the only record of
                    // who the site belongs to once the link is gone.
                    client_name: picked ? picked.client_name : editing.client_name,
                  });
                }}
              >
                <option value="">{t('data.siteClientNone')}</option>
                {clientCompanies.map((c) => (
                  <option key={c.client_id} value={c.client_id}>
                    {c.client_name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[10px] leading-snug text-dim">
                {t('data.siteClientHint')}
              </span>
            </Field>

            {/* Shown only while the site is unlinked. When a company IS
                picked its name is the label, so there is no second box
                called "Klientas" for the carrier to fill in instead of the
                one that counts. */}
            {editing.client_id ? null : (
              <>
                <p className="rounded-[10px] border border-amber/30 bg-amber/5 px-3 py-2
                              text-[11px] leading-snug text-amber">
                  {t('data.siteClientUnlinkedWarn')}
                </p>
                <Field label={t('data.siteClientNameLabel')}>
                  <input
                    className="field"
                    value={editing.client_name}
                    onChange={(e) => setEditing({ ...editing, client_name: e.target.value })}
                  />
                </Field>
              </>
            )}

            <Field label={t('data.siteNameLabel')}>
              <input
                className="field"
                value={editing.site_name}
                onChange={(e) => setEditing({ ...editing, site_name: e.target.value })}
              />
            </Field>
            {/* V-47a: the same two boxes and the same map as the quarry
                form. A carrier setting up twenty-five sites was doing fifty
                lookups in Google Maps before this product had done anything
                for him — with the picker sitting in this repo, wired only
                into the client's own order form. */}
            <CoordinateField
              lat={editing.lat}
              lng={editing.lng}
              onChange={({ lat, lng }) => setEditing({ ...editing, lat, lng })}
              radiusM={Number.parseFloat(editing.geofence_radius_m) || 150}
            />
            <div className="grid grid-cols-2 gap-3">
              {/* The free unloading time agreed for THIS site, above the
                  client's own contract term and the carrier-wide setting.
                  Left empty it is not a zero — it means nothing was
                  negotiated here, and the next tier up applies. */}
              {editing.siteId ? (
                <Field label={t('data.siteDwellFreeLabel')}>
                  <input
                    className="field"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder={t('data.dwellFreeInherit')}
                    value={editing.dwell_free_minutes_site}
                    onChange={(e) => setEditing({ ...editing, dwell_free_minutes_site: e.target.value })}
                  />
                  <span className="mt-1 block text-[10px] leading-snug text-dim">
                    {t('data.siteDwellFreeHint')}
                  </span>
                </Field>
              ) : null}

              <Field label={t('data.phoneLabel')}>
                <input
                  className="field"
                  value={editing.contact_phone}
                  onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })}
                />
              </Field>
              <Field label="Email">
                <input
                  className="field"
                  type="email"
                  value={editing.contact_email}
                  onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })}
                />
              </Field>
            </div>
            {formError ? <ErrorState error={new Error(t(formError))} /> : null}
            {saveSite.isError ? <ErrorState error={saveSite.error} /> : null}
          </div>
        ) : null}
      </Modal>
      <SmartImportModal
        open={importing}
        onClose={() => setImporting(false)}
        entityType="client_site"
        title={t('import.titleSites')}
      />
      <ClientReliabilityModal open={reliabilityOpen} onClose={() => setReliabilityOpen(false)} />
      <PromiseKeepingModal open={promiseOpen} onClose={() => setPromiseOpen(false)} />
    </Panel>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">{label}</span>
      {children}
    </label>
  );
}
